import { Formik, Form } from "formik";
import { FormModel } from "../types/FormTypes";
import { useAtom } from "jotai";
import { formDataAtom } from "../App";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useEffect, useRef } from "react";
import { COMPENSATION_TICKET } from "./Compensation";
import useFocusOnLoad from "../hooks/useFocusOnLoad";

import {
  Box,
  Button,
  handleSubmitClick,
  ErrorList,
  Progressbar,
  RadioGroupForm,
  Heading,
} from "@ogt/ui";

export const VALUE_CODE = "värdekod";
export const BANKDEPOSIT = "bankkonto";

export default function RefundPreference() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const [formData, setFormData] = useAtom(formDataAtom);
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const progress = 70;

  useEffect(() => {
    if (formData.compensation === COMPENSATION_TICKET) {
      setFormData((prev) => ({
        ...prev,
        taxireceipt: [],
        distance: "",
        cartravelfrom: "",
        cartravelto: "",
        carlicenceplate: "",
        carexpenses: [],
      }));
    }
  }, [formData.compensation, setFormData]);

  return (
    <div>
      <Formik<FormModel>
        onSubmit={(values) => {
          setFormData({
            ...formData,
            ...values,
          });

          if (values.refundpreference === VALUE_CODE) {
            navigate("/vardekod");
          } else if (values.refundpreference === BANKDEPOSIT) {
            navigate("/bankinsattning");
          }
        }}
        initialValues={{
          ...formData,
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          refundpreference: Yup.string().test(
            "at-least-one-radio-selected",
            "Vänligen välj ett svarsalternativ för att gå vidare till nästa steg",
            (value) => {
              return value === VALUE_CODE || value === BANKDEPOSIT;
            }
          ),
        })}
      >
        {({ errors, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>
            <Form noValidate>
              <Box>
                <Heading
                  progress={progress}
                  headingRef={h2Ref}
                  focusable
                  id="refundPreference"
                >
                  Hur vill du ha ersättningen utbetald?
                </Heading>

                <p>
                  Om du väljer att få ersättningen som en värdekod får du
                  ersättningen plus 10 kronor extra.
                </p>

                {Object.keys(errors).length > 0 && (
                  <ErrorList errors={errors} />
                )}

                <div className={errors.refundpreference ? "has-error" : ""}>
                  <RadioGroupForm
                    name="refundpreference"
                    ariaLabelledBy="refundpreference"
                    orientation="vertical"
                    variant="box"
                    options={[
                      {
                        id: "withApp",
                        value: VALUE_CODE,
                        label: "Jag vill ha ersättning som en värdekod",
                      },
                      {
                        id: "withTravelcard",
                        value: BANKDEPOSIT,
                        label: "Jag vill ha ersättning till mitt bankkonto",
                      },
                    ]}
                  />
                </div>
              </Box>

              <div className="flex justify-center">
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    onClick={handleSubmitClick(formRef)}
                    className="btn primary"
                    type="submit"
                  >
                    Gå vidare
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}
